'use client';

import { forwardRef, useEffect, useRef } from 'react';

import { GameStatus } from '../engine';
import styles from '../SolarSurge.module.css';

type Props = {
  highScore: number;
  onHold: (on: boolean) => void;
  onStart: () => void;
  onToggleSound: () => void;
  soundOn: boolean;
  status: GameStatus;
};

export const Cabinet = forwardRef<HTMLCanvasElement, Props>(
  ({ highScore, onHold, onStart, onToggleSound, soundOn, status }, ref) => {
    // Suppress the iOS long-press magnifier/callout on the fly button. React attaches touchstart as a
    // passive listener, so preventDefault there is ignored — bind a non-passive native listener directly.
    const thrustRef = useRef<HTMLButtonElement>(null);
    useEffect(() => {
      const button = thrustRef.current;
      if (!button) return undefined;
      const block = (event: TouchEvent) => event.preventDefault();
      button.addEventListener('touchstart', block, { passive: false });
      return () => button.removeEventListener('touchstart', block);
    }, []);

    return (
      <div className={styles.cabinet}>
        <div className={styles.marquee}>
          {highScore > 0 && <span className={styles.best}>BEST {highScore.toFixed(0)} kWh</span>}
          <span>SOLAR SURGE</span>
          <button
            aria-label={soundOn ? 'Sound on, click to mute' : 'Sound off, click to unmute'}
            aria-pressed={soundOn}
            className={styles.sound}
            onClick={onToggleSound}
            type="button"
          >
            <span className={styles.soundLabel}>SOUND</span>
            <span aria-hidden="true" className={soundOn ? `${styles.toggle} ${styles.toggleOn}` : styles.toggle}>
              <span className={styles.toggleKnob} />
            </span>
          </button>
        </div>

        <div className={styles.screen}>
          <canvas aria-label="Solar Surge game screen" className={styles.canvas} ref={ref} />

          {status === 'idle' && (
            <div className={styles.overlay}>
              <p className={styles.overlayTitle}>SOLAR SURGE</p>
              <p className={styles.overlaySub}>
                <span className={styles.showDesktop}>
                  KEEP THE SUN ON THE PANELS
                  <br />
                  DODGE THE CLOUDS · BANK ENERGY
                </span>
                <span className={styles.showMobile}>
                  KEEP THE SUN ON THE PANELS
                  <br />
                  DODGE THE CLOUDS
                  <br />
                  BANK ENERGY
                </span>
              </p>
              <button className={styles.coinBtn} onClick={onStart} type="button">
                PLAY
              </button>
              <p className={`${styles.holdHint} ${styles.blink}`}>HOLD TO RISE · RELEASE TO FALL</p>
            </div>
          )}

          {status === 'ready' && (
            <div
              className={`${styles.overlay} ${styles.overlayClickable}`}
              onClick={onStart}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') onStart();
              }}
              role="button"
              tabIndex={0}
            >
              <p className={styles.overlayTitle}>READY?</p>
              <p className={styles.overlaySub}>
                <span className={styles.showDesktop}>
                  PRESS SPACE OR CLICK
                  <br />
                  TO LAUNCH THE SUN
                </span>
                <span className={styles.showMobile}>
                  HOLD THE BUTTON TO FLY
                  <br />
                  RELEASE BUTTON TO FALL
                </span>
              </p>
            </div>
          )}
        </div>

        {/* Flight control: hold to fly so a thumb never covers the play area. Pointer capture keeps
          thrust on if the thumb drifts. Desktop can also fly with the mouse or spacebar. */}
        <button
          aria-label="Hold to fly"
          className={styles.thrustBtn}
          onContextMenu={(event) => event.preventDefault()}
          onLostPointerCapture={() => onHold(false)}
          onPointerCancel={() => onHold(false)}
          onPointerDown={(event) => {
            event.preventDefault();
            try {
              event.currentTarget.setPointerCapture(event.pointerId);
            } catch {
              /* a capture failure must never block the flight input */
            }
            onHold(true);
          }}
          onPointerUp={() => onHold(false)}
          ref={thrustRef}
          type="button"
        >
          <span aria-hidden="true" className={styles.thrustCap} />
        </button>
      </div>
    );
  }
);
